import { defineStore } from "pinia";
import { mande } from "mande";
import { queryFromObject } from "@/utils/api-functions";

export interface Team {
    id: number;
    title: {
        rendered: string;
    };
    acf: {
        function: string;
        email: boolean;
        tel: string;
        primary_image: string;
    };
}

export interface Project {
    id: number;
    title: {
        rendered: string;
    };
    link: string;
    relative_link: string;
    acf: {
        description: string;
        last_chance: boolean;
        title: string;
        primary_image: string;
        sold_image: string | null;
        special_note?: {
            text: string;
            color: "bg-beige-100" | "bg-beige-200" | "bg-beige-300" | "bg-brown-100" | "bg-brown-200" | "bg-gold";
        };
    };
}

interface EstatePicture {
    id: number;
    order: number;
    orientation: "Landscape" | "Portrait";
    urlLarge: string;
    urlSmall: string;
    urlXXL: string;
}

export interface WpProject {
    id: number;
    post_title: string;
    link: string;
    relative_link: string;
    category?: [
        {
            cat_ID: number;
            cat_name: string;
        },
    ];
    acf: {
        title?: string;
        city?: string;
        location?: string;
        primary_image: string;
        portret_image: string;
        description?: string;
        intro?: string;
        bedrooms?: {
            min: string;
            max: string;
        };
        bathrooms?: {
            min: string;
            max: string;
        };
        area?: {
            min: string;
            max: string;
        };
        special_note?: {
            text: string;
            color: "bg-beige-100" | "bg-beige-200" | "bg-beige-300" | "bg-brown-100" | "bg-brown-200" | "bg-gold";
        };
    };
}

export interface LaudMarker {
    address: string;
    lat: number;
    lng: number;
    zoom: number;
}

export interface WpUnit {
    id: number;
    post_title: string;
    relative_link: string;
    acf: {
        title?: string;
        city?: string;
        type?: "huis" | "appartement" | "grond" | "kantoor" | "commercieel" | "industrieel" | "garage";
        price?: string;
        location?: string;
        image: string;
        portret_image: string;
        description: string;
        bedrooms?: string;
        bathrooms?: string;
        area?: string;
    };
}

export interface WhiseUnit {
    id: number;
    rooms?: number;
    bathRooms?: number;
    pictures?: Array<{ id: number; urlLarge: string }>;
    price?: number;
    city: string;
    name: string;
    currency: string;
    displayPrice: boolean;
    purposeStatus: {
        id: number;
    };
}

export interface City {
    zip: string;
    name: string;
}

interface ApiStore {
    projects: Project[];
    team: Team[];
}

const api = mande("/api");
// let token = document.head.querySelector('meta[name="csrf-token"]');
// if (token) {
//     window.axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
// }
export const useApiStore = defineStore("api", {
    state: () => {
        return {
            projects: [],
            team: [],
        } as ApiStore;
    },

    actions: {
        async getProjects(payload?: { categories?: number[]; categories_exclude?: number[]; per_page?: number; exclude?: number[]; include?: number[] }): Promise<Project[]> {
            const response = await api.get<{ data: Project[] }>(`projects?${queryFromObject(payload)}`);

            return response.data as Project[];
        },
        async getTeam(): Promise<Team[]> {
            const response = await api.get<{ data: Team[] }>("team");

            return response.data as Team[];
        },
        async getWpProjects(payload?: { page?: number; per_page?: number; categories_exclude?: number[]; last_chance?: number }): Promise<WpProject[]> {
            `estates?${queryFromObject({ project: true, ...payload })}`;
            const response = await api.get<{ data: WpProject[] }>(`estates?${queryFromObject({ project: true, ...payload })}`);

            return response.data as WpProject[];
        },
        async getWhiseUnits(payload?: { page?: number; per_page?: number }): Promise<{ data: WhiseUnit[]; total: number; page: number }> {
            const response = await api.get<{ data: WhiseUnit[]; total: number; page: number }>(`w/estates?${queryFromObject(payload)}`);

            return response;
        },
        async getWpUnits(payload?: { page?: number; per_page?: number }): Promise<WpUnit[]> {
            const response = await api.get<{ data: WpUnit[] }>(`estates?${queryFromObject(payload)}`);

            return response.data as WpUnit[];
        },
        async getCities(): Promise<string[]> {
            const response = await api.get<{ data: string[] }>("cities");

            return response.data as string[];
        },
        async getMarkers(): Promise<LaudMarker[]> {
            const response = await api.get<{ data: LaudMarker[] }>("markers");

            return response.data as LaudMarker[];
        },
        async makeAppointment(values: { first_name: string; email: string; phone: string; type: string }): Promise<string[]> {
            const response = await api.post<{ data: string[] }>("appointment", values);

            return response.data as string[];
        },
        async submitViewingDays(values: { first_name: string; email: string; phone: string; option_values: string }): Promise<string[]> {
            const response = await api.post<{ data: string[] }>("viewing-days", values);

            return response.data as string[];
        },
        async keepMePosted(values: { first_name: string; email: string; projects: string[]; mailchimp_id: string[] }): Promise<string[]> {
            const response = await api.post<{ data: string[] }>("keep-me-posted", values);

            return response.data as string[];
        },
        async downloadBrochure(values: { first_name: string; last_name: string; email: string; phone: string }): Promise<string[]> {
            const response = await api.post<{ data: string[] }>("download-brochure", values);

            return response.data as string[];
        },
        async subscribeNewsletter(values: { email: string }): Promise<string[]> {
            const response = await api.post<{ data: string[] }>("newsletter", values);

            return response.data as string[];
        },
    },
});
